import React, { useEffect, useState } from "react";
import { Download, Loader2, Mail, Search, Smartphone, Users } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Subscribers panel — list + filter + CSV export.
 *
 * Lives as a tab on SiteDetail. Defaults to the current site's rows and the
 * "Unsubscribed" filter (which is what the operator most often wants — the
 * suppression list for compliance + audit).
 */

const PAGE_LIMIT = 200;

function StatusBadge({ unsubscribed }) {
  if (unsubscribed) {
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 rounded-sm text-[10px] font-semibold uppercase tracking-wider bg-rose-500/10 text-rose-300 border border-rose-500/30">
        Unsubscribed
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-1.5 py-0.5 rounded-sm text-[10px] font-semibold uppercase tracking-wider bg-emerald-500/10 text-emerald-300 border border-emerald-500/30">
      Active
    </span>
  );
}

function ChannelChip({ channel }) {
  const Icon = channel === "sms" ? Smartphone : Mail;
  return (
    <span className="inline-flex items-center gap-1 text-[11px] uppercase font-medium tracking-wider text-zinc-300">
      <Icon size={12} className="opacity-70" />
      {channel}
    </span>
  );
}

export default function SubscribersTab({ site }) {
  const [rows, setRows] = useState(null);
  const [unsubscribed, setUnsubscribed] = useState("true"); // 'true' | 'false' | ''
  const [channel, setChannel] = useState(""); // '' | 'email' | 'sms'
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch + refetch when filters change
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams();
        params.set("site_id", site.id);
        if (unsubscribed) params.set("unsubscribed", unsubscribed);
        if (channel) params.set("channel", channel);
        if (q) params.set("q", q);
        params.set("limit", String(PAGE_LIMIT));
        const r = await api.get(`/admin/subscribers?${params.toString()}`);
        if (!cancelled) setRows(Array.isArray(r.data) ? r.data : []);
      } catch (e) {
        if (cancelled) return;
        setRows([]);
        const msg = (e && e.response && e.response.data && e.response.data.detail) || "Failed to load subscribers";
        setError(msg);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [site.id, unsubscribed, channel, q]);

  const csvHref = (() => {
    const params = new URLSearchParams();
    params.set("site_id", site.id);
    if (unsubscribed) params.set("unsubscribed", unsubscribed);
    if (channel) params.set("channel", channel);
    if (q) params.set("q", q);
    return `${process.env.REACT_APP_BACKEND_URL}/api/admin/subscribers.csv?${params.toString()}`;
  })();

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-sm font-medium text-zinc-200">
          <Users size={16} className="opacity-70" />
          <span>Subscribers</span>
          <span className="text-zinc-500 text-xs">{rows ? `· ${rows.length}${rows.length === PAGE_LIMIT ? "+" : ""} row${rows.length === 1 ? "" : "s"}` : ""}</span>
        </div>
        <a
          data-testid="subscribers-csv-download"
          href={csvHref}
          className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-sm bg-amber-500/20 border border-amber-500/40 hover:bg-amber-500/30 text-amber-200"
        >
          <Download size={14} /> Download CSV
        </a>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap pb-3 border-b border-zinc-800">
        <div className="flex border border-zinc-800 rounded-sm overflow-hidden text-xs">
          {[
            { value: "true", label: "Unsubscribed", testid: "subscribers-filter-unsub" },
            { value: "false", label: "Active", testid: "subscribers-filter-active" },
            { value: "", label: "All", testid: "subscribers-filter-all" },
          ].map((f) => (
            <button
              key={f.value || "all"}
              data-testid={f.testid}
              onClick={() => setUnsubscribed(f.value)}
              className={`px-3 py-1.5 font-semibold uppercase tracking-wider transition ${
                unsubscribed === f.value ? "bg-zinc-800 text-zinc-100" : "text-zinc-400 hover:text-zinc-200"
              }`}
            >{f.label}</button>
          ))}
        </div>
        <div className="flex border border-zinc-800 rounded-sm overflow-hidden text-xs">
          {[
            { value: "", label: "All channels" },
            { value: "email", label: "Email" },
            { value: "sms", label: "SMS" },
          ].map((c) => (
            <button
              key={c.value || "any"}
              data-testid={`subscribers-channel-${c.value || "all"}`}
              onClick={() => setChannel(c.value)}
              className={`px-3 py-1.5 font-semibold uppercase tracking-wider transition ${
                channel === c.value ? "bg-zinc-800 text-zinc-100" : "text-zinc-400 hover:text-zinc-200"
              }`}
            >{c.label}</button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[240px]">
          <Search size={14} className="absolute left-2.5 top-2 opacity-50 pointer-events-none" />
          <input
            data-testid="subscribers-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by email, phone or name…"
            className="admin-input w-full bg-transparent border border-zinc-800 pl-8 pr-3 py-1.5 rounded-sm text-xs"
          />
        </div>
      </div>

      {/* Body */}
      {loading && (
        <div className="text-center text-xs text-zinc-500 mono py-8">
          <Loader2 className="w-4 h-4 mx-auto mb-2 animate-spin" />
          Loading…
        </div>
      )}
      {error && !loading && (
        <div className="border border-rose-500/40 bg-rose-500/5 text-rose-300 text-xs px-3 py-2 rounded-sm mono">{error}</div>
      )}
      {!loading && !error && rows && rows.length === 0 && (
        <div className="border border-dashed border-zinc-800 rounded-sm p-12 text-center bg-zinc-950/40">
          <Users className="w-8 h-8 text-zinc-700 mx-auto mb-3" />
          <div className="text-sm text-zinc-400 mb-1">No matching subscribers.</div>
          <div className="text-xs text-zinc-600 mono">Adjust the filters above, or wait for sign-ups to roll in.</div>
        </div>
      )}
      {!loading && !error && rows && rows.length > 0 && (
        <div className="border border-zinc-800 rounded-sm divide-y divide-zinc-800 bg-zinc-950/40 overflow-hidden">
          {rows.map((r) => (
            <div
              key={r.id}
              data-testid={`subscribers-row-${r.id}`}
              className="grid grid-cols-12 gap-3 px-3 py-2.5 text-xs items-center hover:bg-zinc-900/40"
            >
              <div className="col-span-2"><ChannelChip channel={r.channel} /></div>
              <div className="col-span-4 truncate">
                <div className="text-zinc-100">{r.email || r.phone || "—"}</div>
                {(r.first_name || r.last_name) && (
                  <div className="text-[10px] text-zinc-500 mt-0.5">{[r.first_name, r.last_name].filter(Boolean).join(" ")}</div>
                )}
              </div>
              <div className="col-span-2"><StatusBadge unsubscribed={r.unsubscribed} /></div>
              <div className="col-span-2 text-[11px] text-zinc-500 mono truncate">
                {r.unsubscribed && r.unsubscribed_at
                  ? `unsub: ${(r.unsubscribed_at || "").slice(0, 10)}`
                  : r.created_at
                  ? `joined: ${(r.created_at || "").slice(0, 10)}`
                  : ""}
              </div>
              <div className="col-span-2 text-[11px] text-zinc-500 truncate" title={r.site_name || ""}>
                {r.site_name || r.site_id}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
